import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Settings, Building2, Users, Bell, Code, AlertTriangle, Trash2, X, Tag, Plus, Edit3, Check, ServerCog, Activity, ListChecks, FileText, KeyRound, UserPlus, Search, Upload, Image, Mail, Layers } from 'lucide-react';
import { useState, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/services/api';
import { uploadMedia } from '@/services/storage';
import { useAuthStore } from '@/stores/authStore';
import { InstallTokenModal } from './agent/InstallTokenModal';

const WIPE_PRODUCTS_PHRASE = 'ZERAR PRODUTOS';

type SettingsTab = 'company' | 'users' | 'categories' | 'departments' | 'agent' | 'notifications' | 'api';

export function SettingsPage() {
  const { data: tenant } = useQuery({ queryKey: ['tenant'], queryFn: () => apiClient.getTenant().then(r => r.data) });
  const { data: users = [] } = useQuery({ queryKey: ['tenant-users'], queryFn: () => apiClient.getTenantUsers().then(r => r.data) });

  const [tab, setTab] = useState<SettingsTab>('company');

  return (
    <div className="container-app py-8">
      <h1 className="text-3xl font-bold flex items-center gap-2"><Settings className="h-7 w-7" />Configurações</h1>

      <div className="flex gap-1 mt-6 border-b border-border">
        {[
          { v: 'company', label: 'Empresa', icon: Building2 },
          { v: 'users', label: 'Usuários', icon: Users },
          { v: 'categories', label: 'Categorias', icon: Tag },
          { v: 'departments', label: 'Departamentos', icon: Layers },
          { v: 'agent', label: 'Agente ETL', icon: ServerCog },
          { v: 'notifications', label: 'Notificações', icon: Bell },
          { v: 'api', label: 'API', icon: Code },
        ].map((t: any) => (
          <button
            key={t.v}
            onClick={() => setTab(t.v)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 ${tab === t.v ? 'border-brand-600 text-brand-600' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            <t.icon className="h-4 w-4" />{t.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === 'company' && tenant && <CompanyForm tenant={tenant} />}
        {tab === 'users' && <UsersTable users={users} />}
        {tab === 'categories' && <CategoriesManager />}
        {tab === 'departments' && <DepartmentsManager />}
        {tab === 'agent' && <AgentEtlSection />}
        {tab === 'notifications' && <NotificationsForm />}
        {tab === 'api' && <ApiSection />}
      </div>
    </div>
  );
}

function CompanyForm({ tenant }: any) {
  const [form, setForm] = useState({
    razao_social: tenant.razao_social,
    nome_fantasia: tenant.nome_fantasia || '',
    phone: tenant.phone || '',
    contact_name: tenant.contact_name || '',
    timezone: tenant.timezone,
    locale: tenant.locale,
  });
  const updateMut = useMutation({
    mutationFn: () => apiClient.updateTenant(form),
    onSuccess: () => toast.success('Empresa atualizada'),
  });
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Dados da empresa</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <Input value={form.razao_social} onChange={e => setForm({ ...form, razao_social: e.target.value })} placeholder="Razão social" />
              <Input value={form.nome_fantasia} onChange={e => setForm({ ...form, nome_fantasia: e.target.value })} placeholder="Nome fantasia" />
              <Input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} placeholder="Contato" />
              <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Telefone" />
              <div className="grid grid-cols-2 gap-3">
                <select value={form.timezone} onChange={e => setForm({ ...form, timezone: e.target.value })} className="h-10 rounded-md border border-input bg-background px-3 text-sm">
                  <option value="America/Sao_Paulo">Brasília (GMT-3)</option>
                  <option value="America/Asuncion">Asunción (GMT-3)</option>
                </select>
                <select value={form.locale} onChange={e => setForm({ ...form, locale: e.target.value })} className="h-10 rounded-md border border-input bg-background px-3 text-sm">
                  <option value="pt-BR">Português (BR)</option>
                  <option value="es-PY">Español (PY)</option>
                </select>
              </div>
              <Button onClick={() => updateMut.mutate()} loading={updateMut.isPending}>Salvar alterações</Button>
            </CardContent>
          </Card>

          <BrandingCard tenant={tenant} />
        </div>

        <BillingDataCard tenant={tenant} />
      </div>

      <DangerZone />
    </div>
  );
}

// -----------------------------------------------------------------------
// Card de dados de faturamento (CNPJ/CPF + endereço)
// Separado do CNPJ da loja para não misturar os contextos.
// -----------------------------------------------------------------------
function BillingDataCard({ tenant }: { tenant: any }) {
  const qc = useQueryClient();

  const addr = useMemo(() => {
    try { return JSON.parse(tenant.address || '{}'); } catch { return {}; }
  }, [tenant.address]);

  const [tipo, setTipo] = useState<'pj' | 'pf'>(tenant.cnpj ? 'pj' : 'pf');
  const [form, setForm] = useState({
    cnpj: tenant.cnpj || '',
    cpf: tenant.cpf || '',
    cep: addr.cep || '',
    logradouro: addr.logradouro || '',
    numero: addr.numero || '',
    complemento: addr.complemento || '',
    bairro: addr.bairro || '',
    cidade: addr.cidade || '',
    estado: addr.estado || '',
  });
  const [cepLoading, setCepLoading] = useState(false);

  // Re-sync form and tipo whenever the tenant data changes (e.g. after save + invalidateQueries)
  useEffect(() => {
    const a = (() => { try { return JSON.parse(tenant.address || '{}'); } catch { return {}; } })();
    setTipo(tenant.cnpj ? 'pj' : 'pf');
    setForm({
      cnpj: tenant.cnpj || '',
      cpf: tenant.cpf || '',
      cep: a.cep || '',
      logradouro: a.logradouro || '',
      numero: a.numero || '',
      complemento: a.complemento || '',
      bairro: a.bairro || '',
      cidade: a.cidade || '',
      estado: a.estado || '',
    });
  }, [tenant.cnpj, tenant.cpf, tenant.address]);

  function upd(k: keyof typeof form, v: string) {
    setForm(prev => ({ ...prev, [k]: v }));
  }

  async function lookupCep(raw: string) {
    const clean = raw.replace(/\D/g, '');
    if (clean.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${clean}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setForm(prev => ({
          ...prev,
          logradouro: data.logradouro ?? prev.logradouro,
          bairro:     data.bairro     ?? prev.bairro,
          cidade:     data.localidade ?? prev.cidade,
          estado:     data.uf         ?? prev.estado,
        }));
        toast.success('Endereço preenchido pelo CEP');
      } else {
        toast.error('CEP não encontrado');
      }
    } catch {
      toast.error('Falha ao consultar CEP');
    } finally {
      setCepLoading(false);
    }
  }

  const updateMut = useMutation({
    mutationFn: () => {
      const doc = (tipo === 'pj' ? form.cnpj : form.cpf).replace(/\D/g, '');
      const address = JSON.stringify({
        cep:          form.cep.replace(/\D/g, ''),
        logradouro:   form.logradouro.trim(),
        numero:       form.numero.trim(),
        ...(form.complemento.trim() ? { complemento: form.complemento.trim() } : {}),
        ...(form.bairro.trim()      ? { bairro:      form.bairro.trim()      } : {}),
        ...(form.cidade.trim()      ? { cidade:      form.cidade.trim()      } : {}),
        ...(form.estado.trim()      ? { estado:      form.estado.trim().toUpperCase() } : {}),
      });
      return apiClient.updateTenant({
        ...(tipo === 'pj' ? { cnpj: doc } : { cpf: doc }),
        address,
      });
    },
    onSuccess: () => {
      toast.success('Dados de faturamento atualizados');
      qc.invalidateQueries({ queryKey: ['tenant'] });
    },
    onError: () => toast.error('Erro ao salvar dados de faturamento'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Dados de faturamento</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          CNPJ/CPF e endereço usados pelo gateway de pagamento para emissão de cobranças.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">

        {/* Tipo PJ / PF */}
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground block mb-1.5">Tipo</label>
          <div className="grid grid-cols-2 gap-2">
            {(['pj', 'pf'] as const).map(t => (
              <button
                key={t}
                type="button"
                onClick={() => setTipo(t)}
                className={`rounded-lg border-2 px-3 py-2 text-sm transition-colors ${
                  tipo === t
                    ? 'border-brand-600 bg-brand-50/40 text-brand-700 font-medium'
                    : 'border-border hover:border-muted-foreground/40 text-muted-foreground'
                }`}
              >
                {t === 'pj' ? 'Pessoa Jurídica (CNPJ)' : 'Pessoa Física (CPF)'}
              </button>
            ))}
          </div>
        </div>

        {/* Documento */}
        {tipo === 'pj' ? (
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground block mb-1.5">CNPJ</label>
            <Input
              placeholder="12.345.678/0001-90"
              value={form.cnpj}
              onChange={e => upd('cnpj', e.target.value)}
            />
          </div>
        ) : (
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground block mb-1.5">CPF</label>
            <Input
              placeholder="123.456.789-00"
              value={form.cpf}
              onChange={e => upd('cpf', e.target.value)}
            />
          </div>
        )}

        {/* Endereço */}
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground block mb-1.5">Endereço</label>
          <div className="space-y-2">

            {/* CEP com botão de busca */}
            <div className="flex gap-2">
              <Input
                placeholder="CEP (ex: 01310-100)"
                value={form.cep}
                onChange={e => upd('cep', e.target.value)}
                onBlur={e => lookupCep(e.target.value)}
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                loading={cepLoading}
                onClick={() => lookupCep(form.cep)}
                title="Buscar endereço pelo CEP"
              >
                <Search className="h-4 w-4" />
                {!cepLoading && <span className="ml-1 hidden sm:inline">Buscar CEP</span>}
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <Input
                placeholder="Logradouro"
                value={form.logradouro}
                onChange={e => upd('logradouro', e.target.value)}
                className="col-span-2"
              />
              <Input
                placeholder="Número"
                value={form.numero}
                onChange={e => upd('numero', e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Bairro"
                value={form.bairro}
                onChange={e => upd('bairro', e.target.value)}
              />
              <Input
                placeholder="Complemento"
                value={form.complemento}
                onChange={e => upd('complemento', e.target.value)}
              />
            </div>

            <div className="grid grid-cols-3 gap-2">
              <Input
                placeholder="Cidade"
                value={form.cidade}
                onChange={e => upd('cidade', e.target.value)}
                className="col-span-2"
              />
              <Input
                placeholder="UF"
                maxLength={2}
                value={form.estado}
                onChange={e => upd('estado', e.target.value.toUpperCase())}
              />
            </div>
          </div>
        </div>

        <Button
          onClick={() => {
            const doc = (tipo === 'pj' ? form.cnpj : form.cpf).replace(/\D/g, '');
            if (!doc) {
              toast.error(tipo === 'pj' ? 'Informe o CNPJ.' : 'Informe o CPF.');
              return;
            }
            if (!form.cep.trim() || !form.logradouro.trim() || !form.numero.trim()) {
              toast.error('Informe CEP, logradouro e número do endereço.');
              return;
            }
            updateMut.mutate();
          }}
          loading={updateMut.isPending}
        >
          Salvar dados de faturamento
        </Button>
      </CardContent>
    </Card>
  );
}

function BrandingCard({ tenant }: { tenant: any }) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const branding = useMemo(() => {
    try { return JSON.parse(tenant.branding || '{}'); } catch { return {}; }
  }, [tenant.branding]);

  const [logoUrl, setLogoUrl] = useState<string | null>(branding.logo_url || null);

  useEffect(() => {
    try {
      const b = JSON.parse(tenant.branding || '{}');
      setLogoUrl(b.logo_url || null);
    } catch { setLogoUrl(null); }
  }, [tenant.branding]);

  async function saveBranding(newBranding: Record<string, any>) {
    await apiClient.updateTenant({ branding: JSON.stringify(newBranding) });
    qc.invalidateQueries({ queryKey: ['tenant'] });
  }

  async function handleFile(file: File) {
    setUploading(true);
    setProgress(0);
    try {
      const url = await uploadMedia(file, 'logo', (p) => setProgress(p.percent));
      setLogoUrl(url);
      await saveBranding({ ...branding, logo_url: url });
      toast.success('Logomarca atualizada');
    } catch (e: any) {
      toast.error(e?.message || 'Falha no upload');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }

  async function handleRemove() {
    const { logo_url: _removed, ...rest } = branding;
    setLogoUrl(null);
    try {
      await saveBranding(rest);
      toast.success('Logomarca removida');
    } catch {
      toast.error('Falha ao remover logomarca');
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Logomarca</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Usada automaticamente nas playlists. Prefira PNG com fundo transparente.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {logoUrl ? (
          <>
            <div
              className="rounded-lg border border-border flex items-center justify-center p-6"
              style={{ background: 'repeating-conic-gradient(#e5e7eb 0% 25%, #fff 0% 50%) 0 0 / 16px 16px' }}
            >
              <img src={logoUrl} alt="Logomarca" className="max-h-28 max-w-full object-contain" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                <Upload className="h-4 w-4" /> Substituir
              </Button>
              <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={handleRemove} disabled={uploading}>
                <Trash2 className="h-4 w-4" /> Remover
              </Button>
            </div>
          </>
        ) : (
          <div
            className={`rounded-lg border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${uploading ? 'border-brand-300 bg-brand-50/30' : 'border-border hover:border-brand-300 hover:bg-brand-50/20'}`}
            onClick={() => !uploading && fileInputRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
          >
            {uploading ? (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Enviando… {progress}%</p>
                <div className="w-full bg-border rounded-full h-1.5">
                  <div className="bg-brand-600 h-1.5 rounded-full transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
            ) : (
              <>
                <Image className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm font-medium">Clique ou arraste a logomarca aqui</p>
                <p className="text-xs text-muted-foreground mt-1">PNG com fundo transparente recomendado · Máx. 5 MB</p>
              </>
            )}
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
        />
      </CardContent>
    </Card>
  );
}

function DangerZone() {
  const [wipeOpen, setWipeOpen] = useState(false);
  return (
    <>
      <Card className="border-danger/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-danger">
            <AlertTriangle className="h-5 w-5" />
            Zona de perigo
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 max-w-xl">
          <div className="flex items-start justify-between gap-4 rounded-md border border-danger/30 bg-danger/5 p-4">
            <div>
              <div className="font-semibold">Zerar todos os produtos</div>
              <p className="text-sm text-muted-foreground mt-1">
                Remove permanentemente todos os produtos cadastrados no tenant.
                Use antes de uma reimportação completa. Esta ação <strong>não pode ser desfeita</strong>.
              </p>
            </div>
            <Button variant="destructive" onClick={() => setWipeOpen(true)} className="shrink-0">
              <Trash2 className="h-4 w-4" />
              Zerar produtos
            </Button>
          </div>
        </CardContent>
      </Card>

      {wipeOpen && <WipeProductsModal onClose={() => setWipeOpen(false)} />}
    </>
  );
}

function WipeProductsModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [confirmation, setConfirmation] = useState('');
  const isValid = confirmation === WIPE_PRODUCTS_PHRASE;

  const wipeMut = useMutation({
    mutationFn: () => apiClient.wipeProducts(confirmation).then(r => r.data),
    onSuccess: (res: { deleted: number }) => {
      toast.success(`${res.deleted} produto(s) removido(s)`);
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['agent-status'] });
      onClose();
    },
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      if (code === 'confirmation_invalid') toast.error('Frase de confirmação incorreta');
      else toast.error('Falha ao zerar produtos');
    },
  });

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4 overflow-y-auto">
      <div className="bg-card rounded-lg shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-bold flex items-center gap-2 text-danger">
            <AlertTriangle className="h-5 w-5" />
            Zerar todos os produtos
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="rounded-md border border-danger/30 bg-danger/5 p-4 text-sm">
            <p className="font-semibold text-danger">Esta ação é irreversível.</p>
            <p className="mt-2 text-muted-foreground">
              Todos os produtos cadastrados serão removidos permanentemente do banco.
              Promoções e slides que referenciam estes produtos podem ficar inconsistentes.
            </p>
          </div>

          <div>
            <label className="text-sm font-medium block mb-2">
              Para confirmar, digite <code className="px-1.5 py-0.5 rounded bg-muted text-foreground font-mono text-xs">{WIPE_PRODUCTS_PHRASE}</code> abaixo:
            </label>
            <Input
              autoFocus
              value={confirmation}
              onChange={e => setConfirmation(e.target.value)}
              placeholder={WIPE_PRODUCTS_PHRASE}
              className={isValid ? 'border-danger focus-visible:ring-danger' : ''}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t bg-muted/20">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            variant="destructive"
            disabled={!isValid}
            loading={wipeMut.isPending}
            onClick={() => wipeMut.mutate()}
          >
            <Trash2 className="h-4 w-4" />
            Zerar produtos
          </Button>
        </div>
      </div>
    </div>
  );
}

type TenantUserRow = {
  id: number;
  email: string;
  full_name: string;
  role: 'master_admin' | 'owner' | 'admin' | 'editor' | 'viewer';
  phone?: string | null;
  is_active: boolean;
  status?: 'pending' | 'active' | 'blocked';
  last_login_at?: string | null;
};

type AssignableRole = 'admin' | 'editor' | 'viewer';

function roleLabel(role: string) {
  switch (role) {
    case 'owner': return 'Proprietário';
    case 'admin': return 'Administrador';
    case 'editor': return 'Editor';
    case 'viewer': return 'Visualizador';
    case 'master_admin': return 'Master Admin';
    default: return role;
  }
}

function UsersTable({ users }: { users: TenantUserRow[] }) {
  const { role: currentRole, user: currentAuthUser } = useAuthStore();
  const qc = useQueryClient();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [editing, setEditing] = useState<TenantUserRow | null>(null);

  const isOwner = currentRole === 'owner' || currentRole === 'master_admin';
  const currentEmail = currentAuthUser?.email?.toLowerCase();

  const deleteMut = useMutation({
    mutationFn: (id: number) => apiClient.deleteTenantUser(id),
    onSuccess: () => {
      toast.success('Usuário removido');
      qc.invalidateQueries({ queryKey: ['tenant-users'] });
    },
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      if (code === 'cannot_delete_self') toast.error('Você não pode remover sua própria conta');
      else if (code === 'cannot_delete_owner') toast.error('O proprietário não pode ser removido');
      else toast.error('Falha ao remover usuário');
    },
  });

  const resendMut = useMutation({
    mutationFn: (id: number) => apiClient.resendInvite(id),
    onSuccess: () => toast.success('Convite reenviado com sucesso'),
    onError: () => toast.error('Falha ao reenviar convite'),
  });

  function onDelete(u: TenantUserRow) {
    if (!window.confirm(`Remover ${u.full_name} (${u.email})? Esta ação revoga o acesso imediatamente.`)) return;
    deleteMut.mutate(u.id);
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Usuários ({users.length})</CardTitle>
          <Button size="sm" onClick={() => setInviteOpen(true)}>
            <UserPlus className="h-4 w-4" />Convidar usuário
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b border-border">
              <tr className="text-left">
                <th className="p-3 text-xs uppercase">Nome</th>
                <th className="p-3 text-xs uppercase">Email</th>
                <th className="p-3 text-xs uppercase">Permissão</th>
                <th className="p-3 text-xs uppercase">Status</th>
                <th className="p-3 text-xs uppercase">Último login</th>
                <th className="p-3 text-xs uppercase text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = currentEmail && u.email.toLowerCase() === currentEmail;
                const isOwnerRow = u.role === 'owner';
                const canEdit = !isSelf && !isOwnerRow && (isOwner || u.role !== 'admin');
                return (
                  <tr key={u.id} className={`border-b border-border/50 ${!u.is_active ? 'opacity-50' : ''}`}>
                    <td className="p-3">{u.full_name}{isSelf && <span className="ml-2 text-xs text-muted-foreground">(você)</span>}</td>
                    <td className="p-3">{u.email}</td>
                    <td className="p-3"><span className="text-xs uppercase font-medium text-brand-700">{roleLabel(u.role)}</span></td>
                    <td className="p-3">
                      {u.status === 'pending'
                        ? <span className="text-xs font-medium text-amber-600">Pendente</span>
                        : u.is_active
                        ? <span className="text-xs font-medium text-emerald-700">Ativo</span>
                        : <span className="text-xs font-medium text-zinc-500">Inativo</span>}
                    </td>
                    <td className="p-3 text-muted-foreground">{u.last_login_at ? new Date(u.last_login_at).toLocaleString('pt-BR') : 'Nunca'}</td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1">
                        {u.status === 'pending' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={resendMut.isPending}
                            onClick={() => resendMut.mutate(u.id)}
                            title="Reenviar convite"
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={!canEdit}
                          onClick={() => setEditing(u)}
                          title={!canEdit ? (isSelf ? 'Não é possível editar a própria conta' : isOwnerRow ? 'Proprietário não pode ser editado' : 'Apenas o proprietário pode editar administradores') : 'Editar'}
                        >
                          <Edit3 className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700"
                          disabled={!canEdit || deleteMut.isPending}
                          onClick={() => onDelete(u)}
                          title={!canEdit ? 'Não é possível remover este usuário' : 'Remover'}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-sm text-muted-foreground">Nenhum usuário cadastrado.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {inviteOpen && <InviteUserModal onClose={() => setInviteOpen(false)} canCreateAdmin={isOwner} />}
      {editing && <EditUserModal user={editing} onClose={() => setEditing(null)} canAssignAdmin={isOwner} />}
    </>
  );
}

function InviteUserModal({ onClose, canCreateAdmin }: { onClose: () => void; canCreateAdmin: boolean }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<'email' | 'password'>('email');
  const [form, setForm] = useState<{ email: string; fullName: string; password: string; role: AssignableRole; phone: string }>({
    email: '',
    fullName: '',
    password: '',
    role: 'editor',
    phone: '',
  });

  const mut = useMutation({
    mutationFn: () => apiClient.inviteTenantUser({
      email: form.email.trim(),
      fullName: form.fullName.trim(),
      role: form.role,
      password: mode === 'password' ? form.password : undefined,
      phone: form.phone.trim() || undefined,
    }),
    onSuccess: () => {
      if (mode === 'email') toast.success('Convite enviado por email. O usuário define a senha ao aceitar.');
      else toast.success('Usuário criado. Compartilhe a senha inicial com ele.');
      qc.invalidateQueries({ queryKey: ['tenant-users'] });
      onClose();
    },
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      const limit = err?.response?.data?.limit;
      if (code === 'email_taken') toast.error('Este email já está cadastrado em outra conta');
      else if (code === 'weak_password') toast.error('A senha deve ter pelo menos 8 caracteres');
      else if (code === 'missing_fields') toast.error('Preencha todos os campos obrigatórios');
      else if (code === 'invalid_role') toast.error('Permissão inválida');
      else if (code === 'user_limit_reached') toast.error(`Limite do plano atingido (${limit} usuários). Faça upgrade para adicionar mais.`);
      else if (code === 'invite_send_failed') toast.error('Falha ao enviar email de convite. Confira o SMTP do Supabase.');
      else toast.error('Falha ao criar usuário');
    },
  });

  const emailOk = /.+@.+\..+/.test(form.email);
  const passwordOk = mode === 'email' || form.password.length >= 8;
  const valid = emailOk && form.fullName.trim().length >= 2 && passwordOk;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4 overflow-y-auto">
      <div className="bg-card rounded-lg shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <UserPlus className="h-5 w-5" />Convidar usuário
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="text-sm font-medium block mb-1">Nome completo</label>
            <Input autoFocus value={form.fullName} onChange={e => setForm({ ...form, fullName: e.target.value })} placeholder="João da Silva" />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Email</label>
            <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="joao@empresa.com" />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Telefone (opcional)</label>
            <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="(11) 99999-0000" />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Permissão</label>
            <select
              value={form.role}
              onChange={e => setForm({ ...form, role: e.target.value as AssignableRole })}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              {canCreateAdmin && <option value="admin">Administrador — gerencia tudo, exceto faturamento e dados da empresa</option>}
              <option value="editor">Editor — cria e edita playlists/produtos</option>
              <option value="viewer">Visualizador — apenas leitura</option>
            </select>
          </div>

          <div className="rounded-md border bg-muted/30 p-3 space-y-2">
            <div className="text-sm font-medium">Como o usuário vai entrar?</div>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                checked={mode === 'email'}
                onChange={() => setMode('email')}
                className="mt-1"
              />
              <span>
                <span className="font-medium">Enviar convite por email (recomendado)</span>
                <span className="block text-xs text-muted-foreground">
                  O usuário recebe um link mágico e define a própria senha ao aceitar.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                checked={mode === 'password'}
                onChange={() => setMode('password')}
                className="mt-1"
              />
              <span>
                <span className="font-medium">Definir senha inicial agora</span>
                <span className="block text-xs text-muted-foreground">
                  Você compartilha email + senha manualmente. Nenhum email é enviado.
                </span>
              </span>
            </label>
          </div>

          {mode === 'password' && (
            <div>
              <label className="text-sm font-medium block mb-1">Senha inicial</label>
              <Input
                type="text"
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                placeholder="Mínimo 8 caracteres"
              />
              <p className="text-xs text-muted-foreground mt-1">O usuário pode trocar depois em &quot;Esqueci minha senha&quot;.</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t bg-muted/20">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button disabled={!valid} loading={mut.isPending} onClick={() => mut.mutate()}>
            <UserPlus className="h-4 w-4" />
            {mode === 'email' ? 'Enviar convite' : 'Criar usuário'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function EditUserModal({ user, onClose, canAssignAdmin }: { user: TenantUserRow; onClose: () => void; canAssignAdmin: boolean }) {
  const qc = useQueryClient();
  const initialRole: AssignableRole = (user.role === 'admin' || user.role === 'editor' || user.role === 'viewer') ? user.role : 'viewer';
  const [form, setForm] = useState({
    fullName: user.full_name,
    phone: user.phone ?? '',
    role: initialRole,
    isActive: user.is_active,
  });

  const mut = useMutation({
    mutationFn: () => apiClient.updateTenantUser(user.id, {
      fullName: form.fullName.trim(),
      phone: form.phone.trim(),
      role: form.role,
      isActive: form.isActive,
    }),
    onSuccess: () => {
      toast.success('Usuário atualizado');
      qc.invalidateQueries({ queryKey: ['tenant-users'] });
      onClose();
    },
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      if (code === 'cannot_modify_self') toast.error('Você não pode editar a própria conta');
      else if (code === 'cannot_modify_owner') toast.error('O proprietário não pode ser editado por aqui');
      else if (code === 'invalid_role') toast.error('Permissão inválida');
      else toast.error('Falha ao atualizar');
    },
  });

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4 overflow-y-auto">
      <div className="bg-card rounded-lg shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Edit3 className="h-5 w-5" />Editar usuário
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="text-sm font-medium block mb-1">Nome completo</label>
            <Input value={form.fullName} onChange={e => setForm({ ...form, fullName: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Email</label>
            <Input value={user.email} disabled />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Telefone</label>
            <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Permissão</label>
            <select
              value={form.role}
              onChange={e => setForm({ ...form, role: e.target.value as AssignableRole })}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              disabled={user.role === 'admin' && !canAssignAdmin}
            >
              {(canAssignAdmin || user.role === 'admin') && <option value="admin">Administrador</option>}
              <option value="editor">Editor</option>
              <option value="viewer">Visualizador</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={e => setForm({ ...form, isActive: e.target.checked })}
            />
            Usuário ativo (desmarque para suspender o acesso sem remover)
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t bg-muted/20">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button loading={mut.isPending} onClick={() => mut.mutate()}>
            <Check className="h-4 w-4" />Salvar
          </Button>
        </div>
      </div>
    </div>
  );
}

type CategoryRow = { nome: string; sort_order: number; created_at?: string };

function CategoriesManager() {
  const qc = useQueryClient();
  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['product-categories-managed'],
    queryFn: () => apiClient.getProductCategoriesManaged().then(r => r.data as CategoryRow[]),
  });

  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState<{ original: string; nome: string } | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['product-categories-managed'] });
    qc.invalidateQueries({ queryKey: ['product-categories'] }); // legacy /products/categories
  };

  const createMut = useMutation({
    mutationFn: () => apiClient.createProductCategory(newName.trim()).then(r => r.data),
    onSuccess: () => {
      toast.success('Categoria criada');
      setNewName('');
      invalidate();
    },
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      if (code === 'nome_ja_existe') toast.error('Já existe uma categoria com esse nome');
      else toast.error('Erro ao criar categoria');
    },
  });

  const renameMut = useMutation({
    mutationFn: () => apiClient.renameProductCategory(editing!.original, editing!.nome.trim()).then(r => r.data),
    onSuccess: () => {
      toast.success('Categoria renomeada (produtos atualizados em cascata)');
      setEditing(null);
      invalidate();
      qc.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      if (code === 'nome_ja_existe') toast.error('Já existe uma categoria com esse nome');
      else toast.error('Erro ao renomear');
    },
  });

  const deleteMut = useMutation({
    mutationFn: ({ nome, force }: { nome: string; force: boolean }) =>
      apiClient.deleteProductCategory(nome, force).then(r => r.data),
    onSuccess: (res: any) => {
      const unset = res?.products_unset ?? 0;
      toast.success(unset > 0 ? `Categoria removida (${unset} produto(s) ficaram sem categoria)` : 'Categoria removida');
      invalidate();
      qc.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (err: any, vars) => {
      const code = err?.response?.data?.error;
      const count = err?.response?.data?.products_count;
      if (code === 'categoria_em_uso') {
        if (window.confirm(`${count} produto(s) usam esta categoria. Excluir mesmo assim e deixar esses produtos sem categoria?`)) {
          deleteMut.mutate({ nome: vars.nome, force: true });
        }
      } else {
        toast.error('Erro ao excluir');
      }
    },
  });

  function onCreate() {
    if (!newName.trim()) return;
    createMut.mutate();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Tag className="h-5 w-5" /> Categorias de produtos
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Categorias usadas no cadastro de produtos. A importação manual cria automaticamente novas categorias detectadas no arquivo Toledo (campo D1).
        </p>
      </CardHeader>
      <CardContent className="space-y-4 max-w-2xl">
        <div className="flex gap-2">
          <Input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Nova categoria (ex: Carnes Bovinas)"
            onKeyDown={e => { if (e.key === 'Enter') onCreate(); }}
          />
          <Button onClick={onCreate} disabled={!newName.trim()} loading={createMut.isPending}>
            <Plus className="h-4 w-4" /> Adicionar
          </Button>
        </div>

        {isLoading ? (
          <div className="text-center py-6 text-muted-foreground text-sm">Carregando…</div>
        ) : categories.length === 0 ? (
          <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
            Nenhuma categoria cadastrada. Adicione acima ou importe um arquivo Toledo para popular automaticamente.
          </div>
        ) : (
          <div className="rounded-md border divide-y">
            {categories.map((c) => (
              <div key={c.nome} className="flex items-center justify-between gap-2 p-3">
                {editing?.original === c.nome ? (
                  <>
                    <Input
                      autoFocus
                      value={editing.nome}
                      onChange={e => setEditing({ ...editing, nome: e.target.value })}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && editing.nome.trim()) renameMut.mutate();
                        if (e.key === 'Escape') setEditing(null);
                      }}
                    />
                    <div className="flex gap-1 shrink-0">
                      <Button
                        size="sm"
                        onClick={() => renameMut.mutate()}
                        disabled={!editing.nome.trim() || editing.nome === editing.original}
                        loading={renameMut.isPending}
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-medium">{c.nome}</div>
                    <div className="flex gap-1 shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing({ original: c.nome, nome: c.nome })}
                      >
                        <Edit3 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => deleteMut.mutate({ nome: c.nome, force: false })}
                        loading={deleteMut.isPending && deleteMut.variables?.nome === c.nome}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ====================================================================
// Aba "Departamentos" — CRUD espelho de Categorias (cascata em products_cache.departamento)
// ====================================================================
type DepartmentRow = { nome: string; sort_order: number; created_at?: string };

function DepartmentsManager() {
  const qc = useQueryClient();
  const { data: departments = [], isLoading } = useQuery({
    queryKey: ['product-departments-managed'],
    queryFn: () => apiClient.getProductDepartmentsManaged().then(r => r.data as DepartmentRow[]),
  });

  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState<{ original: string; nome: string } | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['product-departments-managed'] });
  };

  const createMut = useMutation({
    mutationFn: () => apiClient.createProductDepartment(newName.trim()).then(r => r.data),
    onSuccess: () => {
      toast.success('Departamento criado');
      setNewName('');
      invalidate();
    },
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      if (code === 'nome_ja_existe') toast.error('Já existe um departamento com esse nome');
      else toast.error('Erro ao criar departamento');
    },
  });

  const renameMut = useMutation({
    mutationFn: () => apiClient.renameProductDepartment(editing!.original, editing!.nome.trim()).then(r => r.data),
    onSuccess: () => {
      toast.success('Departamento renomeado (produtos atualizados em cascata)');
      setEditing(null);
      invalidate();
      qc.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      if (code === 'nome_ja_existe') toast.error('Já existe um departamento com esse nome');
      else toast.error('Erro ao renomear');
    },
  });

  const deleteMut = useMutation({
    mutationFn: ({ nome, force }: { nome: string; force: boolean }) =>
      apiClient.deleteProductDepartment(nome, force).then(r => r.data),
    onSuccess: (res: any) => {
      const unset = res?.products_unset ?? 0;
      toast.success(unset > 0 ? `Departamento removido (${unset} produto(s) ficaram sem departamento)` : 'Departamento removido');
      invalidate();
      qc.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (err: any, vars) => {
      const code = err?.response?.data?.error;
      const count = err?.response?.data?.products_count;
      if (code === 'departamento_em_uso') {
        if (window.confirm(`${count} produto(s) usam este departamento. Excluir mesmo assim e deixar esses produtos sem departamento?`)) {
          deleteMut.mutate({ nome: vars.nome, force: true });
        }
      } else {
        toast.error('Erro ao excluir');
      }
    },
  });

  function onCreate() {
    if (!newName.trim()) return;
    createMut.mutate();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Layers className="h-5 w-5" /> Departamentos de produtos
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Departamentos usados no cadastro de produtos. A importação manual e o agente ETL criam automaticamente novos departamentos detectados no ERP.
        </p>
      </CardHeader>
      <CardContent className="space-y-4 max-w-2xl">
        <div className="flex gap-2">
          <Input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Novo departamento (ex: Mercearia)"
            onKeyDown={e => { if (e.key === 'Enter') onCreate(); }}
          />
          <Button onClick={onCreate} disabled={!newName.trim()} loading={createMut.isPending}>
            <Plus className="h-4 w-4" /> Adicionar
          </Button>
        </div>

        {isLoading ? (
          <div className="text-center py-6 text-muted-foreground text-sm">Carregando…</div>
        ) : departments.length === 0 ? (
          <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
            Nenhum departamento cadastrado. Adicione acima ou importe produtos para popular automaticamente.
          </div>
        ) : (
          <div className="rounded-md border divide-y">
            {departments.map((d) => (
              <div key={d.nome} className="flex items-center justify-between gap-2 p-3">
                {editing?.original === d.nome ? (
                  <>
                    <Input
                      autoFocus
                      value={editing.nome}
                      onChange={e => setEditing({ ...editing, nome: e.target.value })}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && editing.nome.trim()) renameMut.mutate();
                        if (e.key === 'Escape') setEditing(null);
                      }}
                    />
                    <div className="flex gap-1 shrink-0">
                      <Button
                        size="sm"
                        onClick={() => renameMut.mutate()}
                        disabled={!editing.nome.trim() || editing.nome === editing.original}
                        loading={renameMut.isPending}
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-medium">{d.nome}</div>
                    <div className="flex gap-1 shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing({ original: d.nome, nome: d.nome })}
                      >
                        <Edit3 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => deleteMut.mutate({ nome: d.nome, force: false })}
                        loading={deleteMut.isPending && deleteMut.variables?.nome === d.nome}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ====================================================================
// Aba "Agente ETL" — hub do addon de sincronização com ERP do cliente
// ====================================================================
function AgentEtlSection() {
  const [showInstallModal, setShowInstallModal] = useState(false);
  const qc = useQueryClient();

  const { data: agents = [] } = useQuery({
    queryKey: ['agent-etl', 'agents'],
    queryFn: () => apiClient.agentEtl.listAgents().then(r => r.data.agents as any[]),
    refetchInterval: 20_000,
  });

  const { data: routines = [] } = useQuery({
    queryKey: ['agent-etl', 'routines'],
    queryFn: () => apiClient.agentEtl.listRoutines().then(r => r.data.routines as any[]),
  });

  const online = agents.filter((a: any) => a.health === 'online').length;
  const registered = agents.filter((a: any) => a.status === 'active').length;
  const pending = agents.filter((a: any) => a.status === 'pending').length;
  const enabledRoutines = routines.filter((r: any) => r.enabled).length;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ServerCog className="h-5 w-5" /> Agentes ETL
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Sincronize produtos do ERP local com a plataforma. Instale o agente no servidor/caixa do mercado e configure rotinas SQL a partir daqui.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status atual */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard
              icon={<Activity className="h-4 w-4" />}
              label="Online agora"
              value={String(online)}
              tone={online > 0 ? 'green' : registered === 0 ? 'gray' : 'red'}
            />
            <StatCard
              icon={<ServerCog className="h-4 w-4" />}
              label="Registrados"
              value={String(registered)}
              tone="gray"
            />
            <StatCard
              icon={<KeyRound className="h-4 w-4" />}
              label="Aguardando instalação"
              value={String(pending)}
              tone={pending > 0 ? 'amber' : 'gray'}
            />
            <StatCard
              icon={<ListChecks className="h-4 w-4" />}
              label="Rotinas ativas"
              value={`${enabledRoutines}/${routines.length}`}
              tone="gray"
            />
          </div>

          {pending > 0 && (
            <Link
              to="/settings/agent"
              className="block rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 hover:bg-amber-100 transition"
            >
              <strong>{pending}</strong> agente{pending > 1 ? 's' : ''} aguardando instalação.
              Clique para ver as instruções e recuperar o token.
            </Link>
          )}

          {/* Atalhos */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Link to="/settings/agent" className="block rounded-lg border border-border bg-card p-4 hover:border-brand-300 hover:shadow-sm transition group">
              <ServerCog className="h-5 w-5 mb-2 text-brand-600 group-hover:scale-110 transition" />
              <div className="font-medium">Lista de agentes</div>
              <div className="text-xs text-muted-foreground mt-0.5">Status, health, revogação</div>
            </Link>
            <Link to="/settings/agent/routines" className="block rounded-lg border border-border bg-card p-4 hover:border-brand-300 hover:shadow-sm transition group">
              <ListChecks className="h-5 w-5 mb-2 text-brand-600 group-hover:scale-110 transition" />
              <div className="font-medium">Rotinas de sync</div>
              <div className="text-xs text-muted-foreground mt-0.5">SQL configurável com Monaco editor</div>
            </Link>
            <Link to="/settings/agent/logs" className="block rounded-lg border border-border bg-card p-4 hover:border-brand-300 hover:shadow-sm transition group">
              <FileText className="h-5 w-5 mb-2 text-brand-600 group-hover:scale-110 transition" />
              <div className="font-medium">Logs de execução</div>
              <div className="text-xs text-muted-foreground mt-0.5">Timeline filtrável por rotina/status</div>
            </Link>
          </div>

          {/* CTA principal */}
          <div className="rounded-lg border border-brand-200 bg-brand-50/40 p-4 flex items-center justify-between gap-4">
            <div>
              <div className="font-medium flex items-center gap-2">
                <KeyRound className="h-4 w-4" /> Adicionar novo agente
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Gere um token de uso único (24h) + baixe o instalador para o servidor do cliente.
              </p>
            </div>
            <Button onClick={() => setShowInstallModal(true)}>
              <Plus className="h-4 w-4" /> Gerar token + baixar agente
            </Button>
          </div>
        </CardContent>
      </Card>

      {showInstallModal && (
        <InstallTokenModal
          onClose={() => {
            setShowInstallModal(false);
            qc.invalidateQueries({ queryKey: ['agent-etl', 'agents'] });
          }}
        />
      )}
    </div>
  );
}

function StatCard({ icon, label, value, tone }: { icon: React.ReactNode; label: string; value: string; tone: 'green' | 'amber' | 'red' | 'gray' }) {
  const toneClass = {
    green: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    red:   'bg-red-50 border-red-200 text-red-800',
    gray:  'bg-zinc-50 border-zinc-200 text-zinc-700',
  }[tone];
  return (
    <div className={`rounded-md border p-3 ${toneClass}`}>
      <div className="flex items-center gap-2 text-xs font-medium opacity-80">{icon}{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

function NotificationsForm() {
  return (
    <Card>
      <CardHeader><CardTitle>Notificações por email</CardTitle></CardHeader>
      <CardContent className="space-y-3 max-w-md">
        {['TV ficou offline', 'Fatura emitida', 'Pagamento confirmado', 'Trial terminando', 'Promoção atualizada'].map(item => (
          <label key={item} className="flex items-center gap-2 text-sm"><input type="checkbox" defaultChecked /> {item}</label>
        ))}
      </CardContent>
    </Card>
  );
}

function ApiSection() {
  return (
    <Card>
      <CardHeader><CardTitle>API Access</CardTitle></CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          API access disponível no plano <strong>Enterprise</strong>. Documentação em <a href="/swagger" className="text-brand-600 underline">/swagger</a>.
        </p>
      </CardContent>
    </Card>
  );
}
